/**
 * Squelettes de chargement partagés pour les écrans-listes de l'ERP.
 *
 * Objectif perf perçue : remplacer les états « Chargement… » textuels par des
 * placeholders qui reproduisent la forme finale du contenu (carte bordée +
 * lignes) → l'œil anticipe la mise en page, la bascule vers les vraies données
 * ne fait plus « sauter » l'écran.
 *
 * S'appuie sur le composant `Skeleton` du design system (@champ-erp/ui) — pas
 * de nouvelle primitive d'animation, on réutilise `animate-pulse` + la palette
 * champagne existante (DRY).
 */

import { Skeleton } from "@champ-erp/ui";

export interface ListeSkeletonProps {
  /** Nombre de lignes fantômes à afficher (défaut 5). */
  rows?: number;
  /** Libellé accessible annoncé aux lecteurs d'écran. */
  label?: string;
  /** Classes supplémentaires appliquées au conteneur. */
  className?: string;
}

/**
 * Liste fantôme : carte bordée blanche contenant N lignes calquées sur les
 * lignes réelles des listes ERP (pastille icône + deux lignes de texte à
 * gauche, valeur/montant à droite). Cohérent avec ventes / cave / stock.
 */
export function ListeSkeleton({ rows = 5, label = "Chargement…", className }: ListeSkeletonProps) {
  return (
    <div
      className={[
        "divide-y divide-champagne-100 overflow-hidden rounded-xl border border-champagne-200 bg-white",
        className ?? "",
      ]
        .filter(Boolean)
        .join(" ")}
      role="status"
      aria-busy="true"
      aria-label={label}
    >
      {Array.from({ length: rows }).map((_, index) => (
        <div key={index} className="flex min-h-[56px] items-center justify-between gap-4 px-4 py-3">
          <div className="flex min-w-0 flex-1 items-center gap-3">
            <Skeleton variant="circle" size={18} />
            <div className="min-w-0 flex-1 space-y-1.5">
              <Skeleton variant="rect" width="42%" height={13} />
              <Skeleton variant="rect" width="66%" height={11} />
            </div>
          </div>
          <Skeleton variant="rect" width={72} height={14} />
        </div>
      ))}
    </div>
  );
}

/**
 * Squelette pleine page — utilisé comme fallback de <Suspense> pendant le
 * chargement d'un chunk de route (le shell reste monté, seule la zone contenu
 * affiche ce placeholder). Un bandeau de titre + une liste fantôme.
 */
export function PageSkeleton() {
  return (
    <div className="mx-auto w-full max-w-6xl px-6 py-8 lg:px-10" aria-busy="true">
      <Skeleton variant="rect" width="34%" height={26} className="mb-2" />
      <Skeleton variant="rect" width="20%" height={14} className="mb-6" />
      <ListeSkeleton rows={6} label="Chargement de la page…" />
    </div>
  );
}
